import React, { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";
import Badge from "../../components/ui-components/Badge";
import { primaryButtonCls, secondaryButtonCls, destructiveButtonCls } from "../../components/ui-components/buttonStyles";
import { toast } from "../../components/ui-components/Toast";
import {
  listPipelines, getPipeline, triggerPipeline, cancelPipeline, pausePipeline, resumePipeline,
  getPipelineRuns, triggerSchedule, pauseSchedule, resumeSchedule,
} from "./api";
import { cronToHuman, formatDuration, formatRelativeTime, resolveJobStatus, stageSummary } from "./utils";
import ForecastHealthPanel from "./ForecastHealthPanel";



const StageSection = ({ stage, jobs, isFirst, isLast, onJobClick, onTrigger, onPauseResume }) => {
  const [expanded, setExpanded] = useState(false);
  const summary = stageSummary(jobs);
  const allPaused = jobs.length > 0 && jobs.every((j) => j.current_status === "paused");
  const anyRunning = jobs.some((j) => j.last_run_status === "running");

  const handleRunStage = (e) => {
    e.stopPropagation();
    jobs.forEach((j) => onTrigger(j.schedule_id));
  };

  const handlePauseResumeStage = (e) => {
    e.stopPropagation();
    jobs.forEach((j) => onPauseResume(j.schedule_id, allPaused));
  };

  return (
    <>
      {/* Stage summary row */}
      <div
        className={`flex items-center h-[52px] pl-5 pr-4 bg-surface-2 border-l-[3px] border-l-text-3 cursor-pointer select-none ${
          !isFirst ? "border-t border-t-border" : ""
        }`}
        onClick={() => setExpanded(!expanded)}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setExpanded(!expanded); } }}
        tabIndex={0}
        role="button"
        aria-expanded={expanded}
        aria-label={`${stage.name} stage`}
      >
        <div className="w-[45%] min-w-0 flex items-center gap-3">
          <FontAwesomeIcon
            icon={expanded ? icon({ name: "chevron-down", family: "sharp", style: "solid" }) : icon({ name: "chevron-right", family: "sharp", style: "solid" })}
            className="text-text-2 text-[10px] w-2.5 shrink-0"
          />
          <span className="text-sm font-semibold text-text">{stage.name}</span>
          <span className="text-xs text-text-2">{summary.jobsLabel}</span>
        </div>
        <div className="w-[20%] flex items-center">
          <div className="w-fit"><Badge status={summary.badge} label={summary.label} /></div>
        </div>
        <div className="w-[20%] text-sm text-text-2">{summary.lastRun}</div>
        <div className="w-[15%] flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
          {jobs.length > 0 && (
            <>
              <button
                type="button"
                onClick={handleRunStage}
                disabled={anyRunning}
                className={`p-1.5 rounded-ctrl transition-colors ${anyRunning ? "text-text-3 cursor-not-allowed" : "text-text-2 hover:text-text hover:bg-surface-2"}`}
                title="Run all jobs in stage"
                aria-label="Run all jobs in stage"
              >
                <FontAwesomeIcon icon={icon({ name: "play", style: "light" })} className="w-3.5 h-3.5" />
              </button>
              <button
                type="button"
                onClick={handlePauseResumeStage}
                className="p-1.5 text-text-2 hover:text-text hover:bg-surface-2 rounded-ctrl transition-colors"
                title={allPaused ? "Resume all jobs" : "Pause all jobs"}
                aria-label={allPaused ? "Resume all jobs" : "Pause all jobs"}
              >
                {allPaused ? (
                  <FontAwesomeIcon icon={icon({ name: "circle-play", style: "light" })} className="w-3.5 h-3.5" />
                ) : (
                  <FontAwesomeIcon icon={icon({ name: "pause", style: "light" })} className="w-3.5 h-3.5" />
                )}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Expanded detail rows */}
      {expanded && (
        <>
          {jobs.length === 0 ? (
            <div className="pl-5 pr-4 py-6 text-sm text-text-2 border-t border-border">No jobs in this stage.</div>
          ) : (
            jobs.map((job, i) => {
              const sourceId = job.metadata?.source_id || job.job_id || "";
              const credType = job.metadata?.credential_type || "";
              const jobStatus = resolveJobStatus(job);
              const isPaused = job.current_status === "paused";
              const isLastRow = isLast && i === jobs.length - 1;
              return (
                <div
                  key={job.schedule_id}
                  className={`flex items-center h-[64px] pl-5 pr-4 border-t border-border cursor-pointer transition-colors text-sm ${
                    jobStatus.badge === "failed" ? "bg-danger-soft hover:bg-primary-soft" : "hover:bg-primary-soft"
                  } ${isLastRow ? "rounded-b-[16px]" : ""}`}
                  onClick={() => onJobClick(job.schedule_id)}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onJobClick(job.schedule_id); } }}
                  tabIndex={0}
                  role="button"
                  aria-label={`Open job ${job.job_name || sourceId}`}
                >
                  <div className="w-[45%] min-w-0 pr-4 pl-[22px]">
                    <div className="font-medium text-text truncate">{job.job_name || sourceId}</div>
                    <div className="text-xs text-text-2 truncate mt-0.5">
                      {sourceId}{credType ? ` · ${credType.replaceAll("_", " ")}` : ""}
                    </div>
                  </div>
                  <div className="w-[20%] flex items-center">
                    <div className="w-fit"><Badge status={jobStatus.badge} label={jobStatus.label} /></div>
                  </div>
                  <div className="w-[20%] text-text-2">
                    {formatRelativeTime(job.last_run_timestamp)}
                  </div>
                  <div className="w-[15%] flex justify-end" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => onTrigger(job.schedule_id)}
                        disabled={job.last_run_status === "running"}
                        className={`p-1.5 rounded-ctrl transition-colors ${job.last_run_status === "running" ? "text-text-3 cursor-not-allowed" : "text-text-2 hover:text-text hover:bg-surface-2"}`}
                        title="Run now"
                        aria-label="Run now"
                      >
                        <FontAwesomeIcon icon={icon({ name: "play", style: "light" })} className="w-3.5 h-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => onPauseResume(job.schedule_id, isPaused)}
                        className="p-1.5 text-text-2 hover:text-text hover:bg-surface-2 rounded-ctrl transition-colors"
                        title={isPaused ? "Resume" : "Pause"}
                        aria-label={isPaused ? "Resume" : "Pause"}
                      >
                        {isPaused ? (
                          <FontAwesomeIcon icon={icon({ name: "circle-play", style: "light" })} className="w-3.5 h-3.5" />
                        ) : (
                          <FontAwesomeIcon icon={icon({ name: "pause", style: "light" })} className="w-3.5 h-3.5" />
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </>
      )}
    </>
  );
};

const PipelineTab = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: pipelines, isLoading: pipelinesLoading } = useQuery({
    queryKey: ["pipelines"],
    queryFn: listPipelines,
    staleTime: 60000,
  });

  const pipelineId = pipelines?.[0]?.id;

  const { data: pipeline } = useQuery({
    queryKey: ["pipeline", pipelineId],
    queryFn: () => getPipeline(pipelineId),
    enabled: !!pipelineId,
    staleTime: 10000,
    refetchInterval: 10000,
  });

  const { data: runs } = useQuery({
    queryKey: ["pipeline-runs", pipelineId],
    queryFn: () => getPipelineRuns(pipelineId),
    enabled: !!pipelineId,
    staleTime: 30000,
    retry: 1,
  });

  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["pipelines"] });
    queryClient.invalidateQueries({ queryKey: ["pipeline"] });
    queryClient.invalidateQueries({ queryKey: ["pipeline-runs"] });
  }, [queryClient]);

  const triggerPipelineMutation = useMutation({
    mutationFn: () => triggerPipeline(pipelineId),
    onSuccess: () => { toast.success("Pipeline triggered"); invalidate(); },
    onError: (err) => toast.error(err?.message || "Failed to trigger pipeline"),
  });

  const cancelPipelineMutation = useMutation({
    mutationFn: () => cancelPipeline(pipelineId),
    onSuccess: () => { toast.success("Pipeline run stopped"); invalidate(); },
    onError: (err) => toast.error(err?.message || "Failed to stop pipeline run"),
  });

  const pauseResumePipelineMutation = useMutation({
    mutationFn: () => {
      const isPaused = pipeline?.status === "paused";
      return isPaused ? resumePipeline(pipelineId) : pausePipeline(pipelineId);
    },
    onSuccess: () => { toast.success("Pipeline updated"); invalidate(); },
    onError: (err) => toast.error(err?.message || "Action failed"),
  });

  const triggerJobMutation = useMutation({
    mutationFn: triggerSchedule,
    onSuccess: () => { toast.success("Job triggered"); invalidate(); },
    onError: (err) => toast.error(err?.message || "Failed to trigger job"),
  });

  const pauseResumeJobMutation = useMutation({
    mutationFn: ({ id, isPaused }) => (isPaused ? resumeSchedule(id) : pauseSchedule(id)),
    onSuccess: (_, { isPaused }) => {
      toast.success(isPaused ? "Job resumed" : "Job paused");
      invalidate();
    },
    onError: (err) => toast.error(err?.message || "Action failed"),
  });

  const handleJobClick = useCallback((id) => navigate(`/data-pipeline/${id}`), [navigate]);
  const handleTriggerJob = useCallback((id) => triggerJobMutation.mutate(id), [triggerJobMutation]);
  const handlePauseResumeJob = useCallback(
    (id, isPaused) => pauseResumeJobMutation.mutate({ id, isPaused }),
    [pauseResumeJobMutation]
  );

  if (pipelinesLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-text-2" />
      </div>
    );
  }

  if (!pipelineId) {
    return (
      <div className="flex flex-col items-center justify-center h-60">
        <FontAwesomeIcon icon={icon({ name: "diagram-project", style: "light" })} className="text-4xl text-text-3 mb-4" />
        <p className="text-lg font-medium text-text mb-1">No pipeline configured</p>
        <p className="text-sm text-text-2">Create a source connection to automatically set up a data pipeline.</p>
      </div>
    );
  }

  const isPaused = pipeline?.status === "paused";
  const stages = pipeline?.stages || [];
  const anyJobRunning = stages.some((s) => (s.Jobs || s.jobs || []).some((j) => j.last_run_status === "running"));
  // Stoppable means either a live run (is_running / a job mid-flight) or a
  // run row stuck on "running" from a process that died — the cancel
  // endpoint clears both, so the button shows for both.
  const runInProgress = pipeline?.is_running || anyJobRunning || pipeline?.current_run?.status === "running";

  return (
    <div className="mr-6">
      {/* Pipeline Header */}
      <div className="bg-card rounded-card border border-card-bd p-6 mb-4">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <h2 className="text-lg font-semibold text-text">{pipeline?.name || "Data Pipeline"}</h2>
              <div className="w-fit"><Badge status={isPaused ? "stopped" : "success"} label={isPaused ? "Paused" : "Active"} /></div>
            </div>
            <p className="text-sm text-text-2">
              {pipeline?.schedule_expression ? cronToHuman(pipeline.schedule_expression) : "No schedule"} · {pipeline?.timezone || "UTC"}
            </p>
          </div>
          <div className="flex items-center gap-2.5">
            {runInProgress && (
              <button
                type="button"
                onClick={() => cancelPipelineMutation.mutate()}
                disabled={cancelPipelineMutation.isLoading}
                className={destructiveButtonCls}
              >
                <FontAwesomeIcon icon={icon({ name: "stop", family: "sharp", style: "solid" })} className="mr-2" />
                Stop Run
              </button>
            )}
            <button
              type="button"
              onClick={() => triggerPipelineMutation.mutate()}
              disabled={runInProgress || isPaused || triggerPipelineMutation.isLoading}
              className={primaryButtonCls}
            >
              <FontAwesomeIcon icon={icon({ name: "play", family: "sharp", style: "solid" })} className="mr-2" />
              Run Now
            </button>
            <button
              type="button"
              onClick={() => pauseResumePipelineMutation.mutate()}
              disabled={pauseResumePipelineMutation.isLoading}
              className={secondaryButtonCls}
            >
              {isPaused ? (
                <FontAwesomeIcon icon={icon({ name: "circle-play", family: "sharp", style: "solid" })} className="mr-2" />
              ) : (
                <FontAwesomeIcon icon={icon({ name: "pause", family: "sharp", style: "solid" })} className="mr-2" />
              )}
              {isPaused ? "Resume" : "Pause"}
            </button>
          </div>
        </div>
      </div>

      {/* Forecast errors — renders nothing when the forecast is clean. Placed above the
          stages because a degraded forecast does not show up in any stage's status. */}
      <ForecastHealthPanel />

      {/* Stages — single card */}
      <div className="bg-card rounded-card border border-card-bd mb-4 overflow-hidden">
        {/* Column header */}
        <div className="flex items-center h-[36px] pl-5 pr-4 text-text-2">
          <span className="w-[45%] uppercase text-xs tracking-wide font-medium">Stage</span>
          <span className="w-[20%] uppercase text-xs tracking-wide font-medium">Status</span>
          <span className="w-[20%] uppercase text-xs tracking-wide font-medium">Last Run</span>
          <span className="w-[15%] uppercase text-xs tracking-wide font-medium text-right pr-2">Actions</span>
        </div>
        {stages.map((stage, i) => (
          <StageSection
            key={stage.stage}
            stage={stage}
            isFirst={i === 0}
            isLast={i === stages.length - 1}
            jobs={stage.Jobs || stage.jobs || []}
            onJobClick={handleJobClick}
            onTrigger={handleTriggerJob}
            onPauseResume={handlePauseResumeJob}
          />
        ))}
      </div>

      {/* Run History */}
      <div className="bg-card rounded-card border border-card-bd overflow-hidden">
        <div className="flex items-center h-[36px] pl-5 pr-4 text-text-2">
          <span className="w-[45%] uppercase text-xs tracking-wide font-medium">Date</span>
          <span className="w-[20%] uppercase text-xs tracking-wide font-medium">Status</span>
          <span className="w-[20%] uppercase text-xs tracking-wide font-medium">Duration</span>
          <span className="w-[15%] uppercase text-xs tracking-wide font-medium">Stages</span>
        </div>
        {(!runs || runs.length === 0) ? (
          <div className="flex flex-col items-center py-10 border-t border-border">
            <FontAwesomeIcon icon={icon({ name: "clock-rotate-left", style: "light" })} className="text-3xl text-text-3 mb-3" />
            <p className="text-sm text-text-2">No pipeline runs yet</p>
          </div>
        ) : (
          runs.map((run) => {
            let stageResults = [];
            try {
              const parsed = run.stage_results
                ? (typeof run.stage_results === "string" ? JSON.parse(run.stage_results) : run.stage_results)
                : [];
              stageResults = Array.isArray(parsed) ? parsed : [];
            } catch (_) { /* malformed JSON — use empty array */ }
            const done = stageResults.filter((s) => s.status === "success").length;
            const runBadge = run.status === "running" ? "running"
              : run.status === "failed" ? "failed"
              : run.status === "success" ? "success"
              : "pending";
            const runLabel = run.status === "running" ? "Running"
              : run.status === "failed" ? "Failed"
              : run.status === "success" ? "Succeeded"
              : "—";
            return (
              <div key={run.run_id} className="flex items-center h-[56px] pl-5 pr-4 border-t border-border text-sm">
                <div className="flex-none w-[45%] text-text">
                  {new Date(run.started_at).toLocaleDateString()} {new Date(run.started_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                </div>
                <div className="flex-none w-[20%]">
                  <div className="w-fit"><Badge status={runBadge} label={runLabel} /></div>
                </div>
                <div className="flex-none w-[20%] text-text-2">
                  {run.completed_at ? formatDuration(new Date(run.completed_at) - new Date(run.started_at)) : "—"}
                </div>
                <div className="flex-none w-[15%] text-text-2">
                  {stageResults.length > 0 ? `${done}/${stageResults.length}` : "—"}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default PipelineTab;
